import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';

import styled from 'styled-components';
import {
  SPWCancelButton,
  SPWDialogActions,
  SPWInput,
  SPWSaveButton,
} from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import RestoreIcon from 'spw-static/images/restore.svg';
import {
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MAX_CATEGORY_NAME_LENGTH,
  MAX_SUB_CATEGORY_NAME_LENGTH,
  PRIVATE_OFFICES_SUBCATEGORY,
  PROGRAMMING,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import {
  IProject,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import SpaceTypeCategoryIconPicker from 'spw-components/space-type-category-icon-picker/index';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteCustomSpaceTypeSubcategories as deleteCustomSpaceTypeSubcategoriesAction,
  getProjectSpaceTypeCategoryUsage as getProjectSpaceTypeCategoryUsageAction,
} from 'spw-redux/modules/project-space-type-categories/actions';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { canDelete } from 'spw-redux/modules/project-space-type-categories/selectors';
import { CanDeleteEnum } from 'spw-shared/types';
import { isNil } from 'ramda';
import {
  currentProject,
  projectColors as selectProjectColors,
} from 'spw-redux/modules/projects/selectors';
import { isCustomSubcategory } from 'spw-projects/utils';
import { isDuplicateCategory } from 'spw-components/worksheet/utils';
import DeleteInputIcon from 'spw-static/images/delete-input-icon.svg';
import { DialogForm } from '../styles';
import { DefaultSpaceTypeCategory } from '../../../models/space-category';
import CustomSpaceCategoryItem from './custom-space-category-item';

interface UpdateSpaceCategoryDialogProps {
  open: boolean;
  spaceTypeCategory?: IProjectSpaceTypeCategory;
  spaceTypeCategories: IProjectSpaceTypeCategory[];
  defaultConfig?: DefaultSpaceTypeCategory;
  project: IProject;
  onCancel?: () => void;
  onSave?: (spaceTypeCategory: IProjectSpaceTypeCategory) => void;
}

const Text = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 20px;
`;

const StepLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
`;

const StepTitle = styled(StepLabel)`
  text-transform: uppercase;
  font-weight: 500;
  font-size: 14px;
  margin-top: 16px;
`;

const EditButton = styled(Button)`
  &&.MuiButton-root {
    min-width: 103px;
    background: ${colors.white};
    box-sizing: border-box;
    border-radius: 4px;
    transform: none;
    color: #00819e;
    text-transform: uppercase;
    padding: 6px 16px;
    font-size: 0.875rem;
  }

  && {
    color: #00819e;
    text-transform: uppercase;
  }
`;

const ColorIcon = styled.div`
  && {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    background-color: ${(props) => props.color} !important;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    span {
      font-style: normal;
      font-weight: bold;
      font-size: 8px;
      /* identical to box height, or 150% */

      text-align: center;
      text-transform: uppercase;

      /* Base/White */

      color: white;
    }
  }
`;

const Input = styled(SPWInput)`
  input {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }

  input::placeholder {
    color: #a8a8a8 !important;
    font-size: 14px !important;
    font-style: normal !important;
  }
`;

const InputFeedback = styled.span`
  margin-top: 21px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  height: 16px;
  color: #f01346;
  display: flex;
`;

const SmallerText = styled(Text)`
  font-size: 14px;
  text-align: justify;
`;

const CautionSpan = styled.span`
  color: #f01346;
`;

const SubcategoryList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SubcategoryListItem = styled.li`
  display: flex;
  align-items: center;
  padding-top: 8px;
  padding-bottom: 8px;
  border-top: 1px solid #e0e0e0;

  &:last-of-type {
    border-bottom: 1px solid #e0e0e0;
  }
`;

const CheckboxContainer = styled.div`
  flex: 1;
  justify-content: flex-end;
  display: flex;
`;

const DeleteContentIcon = styled.img`
  cursor: pointer;
`;

const MAX_SUBCATEGORY_COUNT = 1;

const CATEGORY_NAME_ERROR_MESSAGE = 'Space category name is already in use.';

export const defaultSubcategoryNames: Record<string, string> = {
  [WORKSTATIONS_SUBCATEGORY]: 'Workstations',
  [PRIVATE_OFFICES_SUBCATEGORY]: 'Private Offices',
};

const UpdateSpaceTypeCategoryDialog: React.FC<UpdateSpaceCategoryDialogProps> = ({
  open,
  onCancel,
  onSave,
  spaceTypeCategory,
  spaceTypeCategories,
  defaultConfig,
}) => {
  const [newName, setNewName] = useState<string>();
  const [titleHasError, setTitleHasError] = useState<boolean>(false);
  const [categoryNameError, setCategoryNameError] = useState<string | null>(
    null
  );
  const [newIcon, setNewIcon] = useState<string>();
  const [standardSubcategories, setStandardSubcategories] = useState<
    IProjectSpaceTypeSubcategory[]
  >([]);
  const [customSubcategories, setCustomSubcategories] = useState<
    IProjectSpaceTypeSubcategory[]
  >([]);
  const [deletedSubcategories, setDeletedSubcategories] = useState<{
    [key: number]: boolean;
  }>({});
  const [subcategoryErrors, setSubcategoryErrors] = useState<{
    [key: number]: boolean;
  }>({});
  const dispatch = useDispatch();
  const { getUnit } = useUnitConversion();
  const canDeleteSubcategory = useSelector(canDelete);
  const project = useSelector(currentProject);
  const projectColors = useSelector(selectProjectColors);
  const addSubcategoryRef = useRef(null);
  const subcategories = standardSubcategories.concat(customSubcategories);

  const parentCategoryNames = useMemo(() => {
    return spaceTypeCategories
      .filter((stc: IProjectSpaceTypeCategory) => {
        return stc.id !== spaceTypeCategory?.id;
      })
      .map((stc) => stc.name.trim().toLowerCase());
  }, [spaceTypeCategories, spaceTypeCategory]);

  const takenCategoryNames = useMemo(() => {
    const withCustomSubcategory: string[] = [];

    if (spaceTypeCategories)
      spaceTypeCategories.forEach((stc: IProjectSpaceTypeCategory) => {
        if (stc.id !== spaceTypeCategory?.id)
          withCustomSubcategory.push(stc.name.trim().toLowerCase());
      });

    standardSubcategories
      .filter((subcategory) => subcategory.name !== '')
      .forEach((subcategory) => {
        withCustomSubcategory.push(subcategory.name.trim().toLowerCase());
      });

    customSubcategories.forEach((subcat) => {
      withCustomSubcategory.push(subcat.name.trim().toLowerCase());
    });
    return withCustomSubcategory;
  }, [
    standardSubcategories,
    spaceTypeCategories,
    spaceTypeCategory?.id,
    customSubcategories,
  ]);

  useEffect(() => {
    setNewName(spaceTypeCategory?.name);
    setNewIcon(spaceTypeCategory?.icon);

    if (
      spaceTypeCategory?.subcategories &&
      spaceTypeCategory?.subcategories.length
    ) {
      setStandardSubcategories(
        spaceTypeCategory.subcategories
          .filter(
            (sC: IProjectSpaceTypeSubcategory) =>
              sC.defaultSpaceTypeCategory.category !== CUSTOM_SUBCATEGORY
          )
          .map((subcategory: IProjectSpaceTypeSubcategory) => {
            return {
              ...subcategory,
              name:
                subcategory.name.toLowerCase() ===
                defaultSubcategoryNames[
                  subcategory.defaultSpaceTypeCategory.category
                ].toLowerCase()
                  ? ''
                  : subcategory.name,
            };
          })
      );

      const projectCustomSubcategories = spaceTypeCategory.subcategories?.filter(
        (subcategory: IProjectSpaceTypeSubcategory) =>
          isCustomSubcategory(subcategory)
      );

      if (projectCustomSubcategories?.length) {
        setCustomSubcategories(projectCustomSubcategories);
      } else {
        setCustomSubcategories([
          {
            name: '',
            includeInTotalWorkSeats: false,
            defaultSpaceTypeCategory: {
              category: CUSTOM_SUBCATEGORY,
            },
            parentId: spaceTypeCategory.id,
          },
        ]);
      }
    }
  }, [project.type, spaceTypeCategory]);

  useEffect(() => {
    if (titleHasError && newName?.length > 0) {
      setTitleHasError(false);
    }
  }, [newName, titleHasError]);

  const selectedIcons = useMemo(() => {
    return spaceTypeCategories?.map((sC) => sC.icon);
  }, [spaceTypeCategories]);

  const disabled = useMemo(() => {
    const subcategoriesHaveErrors = Object.values(subcategoryErrors).some(
      (value) => value
    );

    if (!newName?.trim() || categoryNameError || subcategoriesHaveErrors) {
      return true;
    }
    if (Object.keys(deletedSubcategories)?.length) {
      return !Object.values(deletedSubcategories).some((value) => value);
    }

    if (customSubcategories) {
      return customSubcategories.some(
        (subcategory) => !subcategory.name && subcategory.id
      );
    }

    return false;
  }, [
    newName,
    subcategoryErrors,
    deletedSubcategories,
    categoryNameError,
    customSubcategories,
  ]);

  const setDeletedSubcategoriesById = (id: number, value: boolean) => {
    setDeletedSubcategories((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const isDifferentFromDefault = () => {
    if (
      spaceTypeCategory.defaultSpaceTypeCategory.category ===
      INDIVIDUAL_WORKSPACES_CATEGORY
    ) {
      return (
        spaceTypeCategory?.name !== defaultConfig.name ||
        spaceTypeCategory?.icon !== defaultConfig.icon
      );
    }
    return (
      spaceTypeCategory?.name !== defaultConfig?.name ||
      spaceTypeCategory?.icon !== defaultConfig.icon
    );
  };

  const hasChanged = useCallback(() => {
    return (
      spaceTypeCategory?.name !== newName ||
      spaceTypeCategory?.icon !== newIcon ||
      JSON.stringify(spaceTypeCategory?.subcategories) !==
        JSON.stringify([...standardSubcategories, ...customSubcategories]) ||
      Object.values(deletedSubcategories).some((v) => v)
    );
  }, [
    customSubcategories,
    newIcon,
    newName,
    deletedSubcategories,
    standardSubcategories,
    spaceTypeCategory,
  ]);

  const isMarkedForDelete = useCallback(
    (id: number) => {
      return !isNil(deletedSubcategories[id]);
    },
    [deletedSubcategories]
  );

  const handleOnClose = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleIconSelected = (icon: any) => {
    setNewIcon(icon);
  };

  const handleOnAddSubCategory = () => {
    setCustomSubcategories((prevValue: IProjectSpaceTypeSubcategory[]) => [
      ...prevValue,
      {
        name: '',
        includeInTotalWorkSeats: false,
        parentId: null,
        defaultSpaceTypeCategory: prevValue[0].defaultSpaceTypeCategory,
      },
    ]);
    setTimeout(
      () => addSubcategoryRef.current?.scrollIntoView({ behavior: 'smooth' }),
      50
    );
  };

  const updateSubcategoryAndCategoryErrors = useCallback(
    (
      previousSubcategory: IProjectSpaceTypeSubcategory,
      newSubcategory: IProjectSpaceTypeSubcategory
    ) => {
      setSubcategoryErrors((prevErrors) => {
        const oldSubcategoryName = previousSubcategory.name
          .trim()
          .toLowerCase();

        const subcategoriesWithSameOldName = subcategories.filter(
          (subcategory) => {
            if (!isCustomSubcategory(subcategory) && !subcategory.name) {
              return (
                subcategory.id !== previousSubcategory.id &&
                defaultSubcategoryNames[
                  subcategory.defaultSpaceTypeCategory.category
                ].toLowerCase() === oldSubcategoryName
              );
            }
            return (
              subcategory.id !== previousSubcategory.id &&
              subcategory.name.trim().toLowerCase() === oldSubcategoryName
            );
          }
        );

        const newErrors = {
          [newSubcategory.id]: isDuplicateCategory(
            spaceTypeCategories,
            subcategories,
            newSubcategory.id,
            newSubcategory.name
          ),
        };

        if (
          subcategoriesWithSameOldName?.length === 1 &&
          oldSubcategoryName !== newName.trim().toLowerCase() &&
          !parentCategoryNames.includes(oldSubcategoryName)
        ) {
          newErrors[subcategoriesWithSameOldName[0].id] = false;
        }

        return {
          ...prevErrors,
          ...newErrors,
        };
      });

      const newSubcategoryName = newSubcategory.name.trim().toLowerCase();
      const categoryName = newName.trim().toLowerCase();

      const subcategoriesWithSameName = subcategories.filter(
        (subcategory) =>
          subcategory.id !== newSubcategory.id &&
          subcategory.name.trim().toLowerCase() === newName.trim().toLowerCase()
      );

      if (
        (newSubcategoryName !== categoryName ||
          newSubcategoryName === categoryName) &&
        !subcategoriesWithSameName.length &&
        !parentCategoryNames.includes(categoryName)
      ) {
        setCategoryNameError(null);
      }
    },
    [subcategories, spaceTypeCategories, newName, parentCategoryNames]
  );

  const handleCategoryUpdate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const categoryName = event.target.value.substring(0, 22);
      if (
        categoryName.trim() &&
        takenCategoryNames.includes(categoryName.trim().toLowerCase())
      ) {
        setCategoryNameError(CATEGORY_NAME_ERROR_MESSAGE);
      } else {
        setCategoryNameError(null);
      }
      setNewName(categoryName);

      const subcategoriesWithSameName = subcategories.filter(
        (subcategory) =>
          categoryName &&
          subcategory.name.trim().toLowerCase() ===
            categoryName.trim().toLowerCase()
      );

      if (subcategoriesWithSameName.length === 1) {
        setSubcategoryErrors({
          ...subcategoryErrors,
          [subcategoriesWithSameName[0].id]: false,
        });
      }
    },
    [takenCategoryNames, subcategoryErrors, subcategories]
  );

  const deleteCategoryInputContent = useCallback(() => {
    const subcategoriesWithSameOldName = subcategories.filter(
      (subcategory) =>
        subcategory.name.trim().toLowerCase() === newName.trim().toLowerCase()
    );

    if (subcategoriesWithSameOldName.length === 1) {
      setSubcategoryErrors({
        ...subcategoryErrors,
        [subcategoriesWithSameOldName[0].id]: false,
      });
    }

    setCategoryNameError(null);
    setNewName('');
  }, [subcategories, newName, subcategoryErrors]);

  const handleStandardSubcategoryUpdate = useCallback(
    (index: number, newSubcategory: IProjectSpaceTypeSubcategory) => {
      const previousSubcategory = standardSubcategories.find(
        (subcategory) => subcategory.id === newSubcategory.id
      );

      const newSubcategoryName = newSubcategory.name
        ? newSubcategory.name
        : defaultSubcategoryNames[
            newSubcategory.defaultSpaceTypeCategory.category
          ];

      updateSubcategoryAndCategoryErrors(
        {
          ...previousSubcategory,
          name: previousSubcategory.name
            ? previousSubcategory.name
            : defaultSubcategoryNames[
                previousSubcategory.defaultSpaceTypeCategory.category
              ],
        },
        {
          ...newSubcategory,
          name: newSubcategoryName,
        }
      );

      setStandardSubcategories((prevValue) => [
        ...prevValue.map(
          (
            projectSpaceTypeSubcategory: IProjectSpaceTypeSubcategory,
            subcategoryIndex: number
          ) =>
            subcategoryIndex === index
              ? newSubcategory
              : projectSpaceTypeSubcategory
        ),
      ]);
    },
    [standardSubcategories, updateSubcategoryAndCategoryErrors]
  );

  const handleCustomSubCategoryUpdate = useCallback(
    (index: number, newSubcategory: IProjectSpaceTypeSubcategory) => {
      const previousCustomSubcategory = customSubcategories.find(
        (customSubcategory) => customSubcategory.id === newSubcategory.id
      );
      updateSubcategoryAndCategoryErrors(
        previousCustomSubcategory,
        newSubcategory
      );

      if (newSubcategory.id) {
        setCustomSubcategories((prevValue) => [
          ...prevValue.map(
            (projectSpaceTypeSubcategory: IProjectSpaceTypeSubcategory) =>
              projectSpaceTypeSubcategory.id === newSubcategory.id
                ? newSubcategory
                : projectSpaceTypeSubcategory
          ),
        ]);
      } else {
        setCustomSubcategories((prevValue) => [
          ...prevValue.slice(0, index),
          newSubcategory,
          ...prevValue.slice(index + 1),
        ]);
      }
    },
    [customSubcategories, updateSubcategoryAndCategoryErrors]
  );

  const hasSubcategories = useMemo(
    () =>
      spaceTypeCategory?.defaultSpaceTypeCategory.category ===
      INDIVIDUAL_WORKSPACES_CATEGORY,
    [spaceTypeCategory?.defaultSpaceTypeCategory.category]
  );

  const handleOnSave = useCallback(() => {
    if (onSave) {
      if (!newName || newName.length === 0) {
        setTitleHasError(true);
        return;
      }

      if (!hasChanged()) {
        onCancel();
        return;
      }

      if (hasSubcategories) {
        const subcategoriesToDelete = Object.keys(deletedSubcategories)
          .map((key) => parseInt(key, 10))
          .filter((id) => deletedSubcategories[id]);

        const defaultSubcategories = standardSubcategories.map(
          (subcategory) => {
            return {
              ...subcategory,
              name:
                subcategory.name === ''
                  ? defaultSubcategoryNames[
                      subcategory.defaultSpaceTypeCategory.category
                    ]
                  : subcategory.name,
            };
          }
        );

        if (subcategoriesToDelete?.length) {
          const newCustomSubcategories = customSubcategories?.filter(
            (customSubcategory) =>
              !subcategoriesToDelete.includes(customSubcategory.id)
          );

          dispatch(
            deleteCustomSpaceTypeSubcategoriesAction(
              spaceTypeCategory.id,
              subcategoriesToDelete,
              () => {
                onSave({
                  ...spaceTypeCategory,
                  name: newName.trim(),
                  icon: newIcon,
                  subcategories: [
                    ...defaultSubcategories,
                    ...newCustomSubcategories,
                  ],
                });
              }
            )
          );
        } else {
          const nonEmptyCustomSubcategories =
            customSubcategories?.filter(
              (subcategory: IProjectSpaceTypeSubcategory) => subcategory.name
            ) || [];

          onSave({
            ...spaceTypeCategory,
            name: newName.trim(),
            icon: newIcon,
            subcategories: [
              ...defaultSubcategories,
              ...nonEmptyCustomSubcategories,
            ],
          });
        }
      } else {
        onSave({
          ...spaceTypeCategory,
          name: newName.trim(),
          icon: newIcon,
        });
      }
    }
  }, [
    dispatch,
    newName,
    hasChanged,
    deletedSubcategories,
    spaceTypeCategory,
    customSubcategories,
    standardSubcategories,
    newIcon,
    hasSubcategories,
    onCancel,
    onSave,
  ]);

  const handleOnReset = () => {
    setNewName(defaultConfig.name);
    setNewIcon(defaultConfig.icon);
  };

  const handleDeleteSubcategory = (id: number) => {
    dispatch(getProjectSpaceTypeCategoryUsageAction(id));
    setDeletedSubcategoriesById(id, false);
  };

  const handleRemoveFromDelete = (id: number) => {
    setDeletedSubcategories((prev) => {
      return Object.keys(prev)
        .filter((key) => key !== id.toString())
        .reduce((map, key) => {
          const newKey = parseInt(key, 10);
          return {
            ...map,
            [newKey]: prev[newKey],
          };
        }, {});
    });
  };

  const deleteInputContent = useCallback(
    (index: number) => {
      const previousSubcategory = standardSubcategories[index];

      updateSubcategoryAndCategoryErrors(previousSubcategory, {
        ...previousSubcategory,
        name:
          defaultSubcategoryNames[
            previousSubcategory.defaultSpaceTypeCategory.category
          ],
      });

      setStandardSubcategories((prevValue) => [
        ...prevValue.map(
          (
            projectSpaceTypeSubcategory: IProjectSpaceTypeSubcategory,
            subcategoryIndex: number
          ) =>
            subcategoryIndex === index
              ? { ...projectSpaceTypeSubcategory, name: '' }
              : projectSpaceTypeSubcategory
        ),
      ]);
    },
    [standardSubcategories, updateSubcategoryAndCategoryErrors]
  );

  if (!spaceTypeCategory) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={() => handleOnClose()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableScrollLock
    >
      <DialogTitle id="alert-dialog-title">Space Category</DialogTitle>
      <DialogContent style={{ padding: '0 24px 8px 24px' }}>
        <Text style={{ marginBottom: '16px' }}>
          Renaming space categories will update everywhere in this project.
        </Text>
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <StepTitle style={{ marginBottom: '8px' }}>
            Default Settings
          </StepTitle>
          <ListItem
            style={{
              border: '1px solid #E0E0E0',
              borderRadius: '4px',
              paddingRight: 0,
            }}
          >
            <ListItemIcon style={{ minWidth: '40px' }}>
              <ColorIcon color={defaultConfig?.color}>
                <img
                  src={`/images/${defaultConfig?.icon}.svg`}
                  alt={defaultConfig.name}
                />
              </ColorIcon>
            </ListItemIcon>
            <ListItemText primary={defaultConfig?.name} />
            {isDifferentFromDefault() && (
              <EditButton variant="text" onClick={() => handleOnReset()}>
                <img
                  src={RestoreIcon}
                  alt="Restore"
                  style={{ marginRight: '9px' }}
                />
                Restore
              </EditButton>
            )}
          </ListItem>
          <StepTitle style={{ marginBottom: '16px', marginTop: '24px' }}>
            Custom Settings
          </StepTitle>
          <Input
            label="Space Category Name"
            error={titleHasError || !isNil(categoryNameError)}
            onChange={handleCategoryUpdate}
            value={newName || ''}
            placeholder=""
            InputProps={{
              inputProps: {
                maxLength: MAX_CATEGORY_NAME_LENGTH,
              },
              endAdornment:
                newName?.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="reset title"
                      onClick={deleteCategoryInputContent}
                      edge="end"
                      size="large"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
          <InputFeedback>{categoryNameError}</InputFeedback>
          <SpaceTypeCategoryIconPicker
            currentIcon={spaceTypeCategory.icon}
            selectedIcon={newIcon}
            selectedIconColor={
              projectColors[
                spaceTypeCategory?.defaultSpaceTypeCategory.category
              ] as string
            }
            selectedIcons={selectedIcons}
            defaultIcon={defaultConfig.icon}
            onIconSelected={handleIconSelected}
          />
          {hasSubcategories && (
            <div>
              <StepTitle style={{ marginBottom: '8px', marginTop: '24px' }}>
                Subcategories
              </StepTitle>
              <SmallerText>
                Check the box if the space subcategory is included in total work
                seats. Do not check if subcategory is only included in sq{' '}
                {getUnit('linear')} and not total work seat allocation.
              </SmallerText>
              <SmallerText style={{ marginTop: '8px', marginBottom: '16px' }}>
                <CautionSpan>
                  Changing this after entering data{' '}
                  {project.type === PROGRAMMING
                    ? 'on worksheets'
                    : 'on floor plans'}{' '}
                  will affect all calculations.
                </CautionSpan>
              </SmallerText>
              <SubcategoryList>
                {standardSubcategories?.map(
                  (
                    subcategory: IProjectSpaceTypeSubcategory,
                    index: number
                  ) => {
                    const isDeleteIconVisible =
                      subcategory.name.trim() &&
                      subcategory.name.trim() !==
                        defaultSubcategoryNames[
                          subcategory.defaultSpaceTypeCategory.category
                        ].trim();

                    return (
                      <SubcategoryListItem
                        key={subcategory.id}
                        style={
                          customSubcategories.length &&
                          isMarkedForDelete(customSubcategories[0]?.id)
                            ? {
                                borderBottomColor: 'transparent',
                                padding: '4px 0',
                              }
                            : { padding: '4px 0' }
                        }
                      >
                        <div
                          style={{
                            flex: '1',
                          }}
                        >
                          <Input
                            onChange={(event) => {
                              handleStandardSubcategoryUpdate(index, {
                                ...subcategory,
                                name: event.target.value,
                              });
                            }}
                            style={{ width: '100%', height: '37px' }}
                            error={
                              !isNil(subcategoryErrors[subcategory.id]) &&
                              subcategoryErrors[subcategory.id]
                            }
                            value={subcategory.name || ''}
                            placeholder={`${
                              defaultSubcategoryNames[
                                subcategory.defaultSpaceTypeCategory.category
                              ]
                            } (Default)`}
                            size="small"
                            InputProps={{
                              inputProps: {
                                maxLength: MAX_SUB_CATEGORY_NAME_LENGTH,
                              },
                              endAdornment: isDeleteIconVisible ? (
                                <DeleteContentIcon
                                  alt="delete-content-icon"
                                  src={DeleteInputIcon}
                                  onClick={() => {
                                    deleteInputContent(index);
                                  }}
                                />
                              ) : null,
                            }}
                          />
                          {!isNil(subcategoryErrors[subcategory.id]) &&
                            subcategoryErrors[subcategory.id] && (
                              <InputFeedback style={{ marginTop: 4 }}>
                                {CATEGORY_NAME_ERROR_MESSAGE}
                              </InputFeedback>
                            )}
                        </div>

                        <Box display="flex" alignItems="center">
                          <CheckboxContainer>
                            {subcategory.includeInTotalWorkSeats}
                            <FormControlLabel
                              value="start"
                              control={
                                <Checkbox
                                  color="primary"
                                  disabled
                                  checked={subcategory.includeInTotalWorkSeats}
                                />
                              }
                              label="Include in total work seats"
                              labelPlacement="start"
                            />
                          </CheckboxContainer>
                        </Box>
                      </SubcategoryListItem>
                    );
                  }
                )}
              </SubcategoryList>
              <SubcategoryList>
                {customSubcategories?.map(
                  (
                    subcategory: IProjectSpaceTypeSubcategory,
                    index: number
                  ) => (
                    <CustomSpaceCategoryItem
                      id={subcategory.id}
                      key={subcategory.id || `new-subcategory-${index}`}
                      loading={canDeleteSubcategory === CanDeleteEnum.QUERYING}
                      markedForDelete={isMarkedForDelete(subcategory.id)}
                      onRemoveFromDelete={handleRemoveFromDelete}
                      confirmationValue={deletedSubcategories[subcategory.id]}
                      onConfirmationChange={(val) => {
                        setDeletedSubcategoriesById(subcategory.id, val);
                      }}
                    >
                      <SubcategoryListItem style={{ borderTop: 'none' }}>
                        <div style={{ flex: '1' }}>
                          <Input
                            onChange={(event) =>
                              handleCustomSubCategoryUpdate(index, {
                                ...subcategory,
                                name: event.target.value,
                              })
                            }
                            style={{ width: '100%', height: '37px' }}
                            error={
                              !isNil(subcategoryErrors[subcategory.id]) &&
                              subcategoryErrors[subcategory.id]
                            }
                            value={subcategory.name || ''}
                            placeholder="Add a subcategory"
                            size="small"
                            InputProps={{
                              inputProps: {
                                maxLength: MAX_SUB_CATEGORY_NAME_LENGTH,
                              },
                            }}
                          />
                          {!isNil(subcategoryErrors[subcategory.id]) &&
                            subcategoryErrors[subcategory.id] && (
                              <InputFeedback style={{ marginTop: 4 }}>
                                {CATEGORY_NAME_ERROR_MESSAGE}
                              </InputFeedback>
                            )}
                        </div>
                        <Box
                          display="flex"
                          alignItems="center"
                          marginBottom={
                            subcategoryErrors[subcategory.id] ? '14px' : '0px'
                          }
                        >
                          {subcategory.id && (
                            <IconButton
                              aria-label="Delete a subcategory"
                              onClick={() =>
                                handleDeleteSubcategory(subcategory.id)
                              }
                              size="large"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          <CheckboxContainer>
                            <FormControlLabel
                              value="start"
                              disabled={!subcategory.name}
                              control={
                                <Checkbox
                                  data-testid="custom_subcategory_include_in_total_workseats_checkbox"
                                  color="primary"
                                  checked={subcategory.includeInTotalWorkSeats}
                                  onChange={(e) =>
                                    handleCustomSubCategoryUpdate(index, {
                                      ...subcategory,
                                      includeInTotalWorkSeats: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label="Include in total work seats"
                              labelPlacement="start"
                            />
                          </CheckboxContainer>
                        </Box>
                      </SubcategoryListItem>
                    </CustomSpaceCategoryItem>
                  )
                )}
              </SubcategoryList>
              {MAX_SUBCATEGORY_COUNT !== 1 && (
                <EditButton
                  ref={addSubcategoryRef}
                  variant="text"
                  style={{ marginTop: '8px' }}
                  onClick={() => handleOnAddSubCategory()}
                >
                  <AddCircleIcon /> &nbsp;Add Subcategory
                </EditButton>
              )}
            </div>
          )}
        </DialogForm>
      </DialogContent>
      <SPWDialogActions className="small-bottom-padding">
        <SPWSaveButton onClick={handleOnSave} disabled={disabled} name="Save" />
        <SPWCancelButton onClick={handleOnClose} customColor={colors.black} />
      </SPWDialogActions>
    </Dialog>
  );
};

export default UpdateSpaceTypeCategoryDialog;
